import * as analytics from 'analytics/Analytics';
import {ActionMethod, ActionOrigin, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ParameterStructuredTableParameterSelectionModal, {
  trackAddParametersModalAnalyticsEvent,
} from 'notebook/integrations/procedures/add-parameters-to-table/ParameterStructuredTableParameterSelectionModal';
import ParameterStructuredTableAdapter, {
  ParameterStructuredTableRowMetadata,
  ParameterStructuredTable,
  ParameterStructuredTableMetadata,
} from 'notebook/integrations/procedures/ParameterStructuredTableAdapter';
import {
  StructuredTableAddOperationOptions,
  StructuredTableAddOperationParameters,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import {ParameterSettings} from 'procedures/design/step-config/ProcedureStepConfigEditorContext';
import {ParameterUsages} from 'procedures/ProcedureDomainTypes';
import legacyRunModal from 'ui-library/legacyRunModal';

export interface AddParametersFromMethodParams extends StructuredTableAddOperationOptions {
  destinationTable: ParameterStructuredTable;
  addRowsFn: StructuredTableAddOperationParameters<
    ParameterStructuredTableMetadata,
    ParameterStructuredTableRowMetadata
  >['addFullRows'];
  parameterSettings?: ParameterSettings;
  parameterUsages?: ParameterUsages;
}

/**
 * Allows the user to add rows to a table from another table. This function handles the entire
 * operation, from opening the "Add samples" modal through to adding the rows to the table.
 */
export default async function addParametersFromMethod(props: AddParametersFromMethodParams): Promise<void> {
  const {destinationTable} = props;

  const sessionId = analytics.makeAnalyticsEventId();
  trackAddParametersModalAnalyticsEvent(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, destinationTable, {
    session_id: sessionId,
    action_origin: ActionOrigin.NOTEBOOK_UNDETERMINED_STRUCTURED_TABLE,
    action_method: ActionMethod.BUTTON_CLICK,
  });
  const addParametersCommand = await legacyRunModal(ParameterStructuredTableParameterSelectionModal, {
    destinationTable,
    sessionId,
    parameterTableMode: destinationTable.metadata.mode,
    parameterSettings: props.parameterSettings,
    parameterUsages: props.parameterUsages,
    fileId: destinationTable.fileId,
    isConfig: destinationTable.isConfig,
  });

  if (addParametersCommand) {
    // Get the list of parameters selected from the modal
    const {parameters} = addParametersCommand;

    // Create and add rows to the table for each of the selected parameters
    const adapter = new ParameterStructuredTableAdapter(StructuredTableKind.PARAMETER);
    adapter.addParametersToTable(destinationTable, parameters, props.addRowsFn);
  }
}
